function setImportantStyle(element, prop, value) {
  if (!element || !element.style) return;
  element.style.setProperty(prop, value, 'important');
}

function getMonthLonghand(instance) {
  const months = instance && instance.l10n && instance.l10n.months && instance.l10n.months.longhand;
  if (!Array.isArray(months) || months.length < 12) return null;
  return months;
}

function safeChangeMonth(instance, monthIndex) {
  if (!instance) return;
  if (typeof instance.changeMonth === 'function') {
    // Flatpickr expects 0-11 when isOffset=false.
    instance.changeMonth(monthIndex, false);
    return;
  }

  instance.currentMonth = monthIndex;
  if (typeof instance.redraw === 'function') instance.redraw();
}

function safeChangeYear(instance, year) {
  if (!instance) return;
  if (typeof instance.changeYear === 'function') {
    instance.changeYear(year);
    return;
  }

  instance.currentYear = year;
  if (typeof instance.redraw === 'function') instance.redraw();
}

function stopHeaderControlClickFromClosingCalendar(event) {
  if (!event) return;
  event.stopPropagation();
}

function applyYearFromInput(instance, inputEl) {
  const nextYear = parseInt(inputEl && inputEl.value, 10);
  if (Number.isFinite(nextYear)) safeChangeYear(instance, nextYear);
}

function isLikelyFullYear(value) {
  const text = String(value || '').trim();
  if (text.length < 4) return false;
  const year = parseInt(text, 10);
  return Number.isFinite(year) && year >= 1000 && year <= 9999;
}

/**
 * Ensure the Flatpickr month/year header is always visible AND clickable.
 *
 * Some global form styles can hide/restyle Flatpickr's default year <input>.
 * We render our own month <select> and year <input> inside the header and
 * force key styles inline (with !important) to keep it readable and usable.
 */
export default function ensureDhdsMonthYearHeader(instance) {
  if (!instance || !instance.calendarContainer) return;

  const container = instance.calendarContainer;
  const currentMonthEl = container.querySelector('.flatpickr-current-month');
  if (!currentMonthEl) return;

  const doc = currentMonthEl.ownerDocument || document;
  const monthLonghand = getMonthLonghand(instance) || [];
  const yearText = String(instance.currentYear ?? '');
  const monthIndexText = String(instance.currentMonth ?? 0);

  const existingYearInput = currentMonthEl.querySelector('.dhds-flatpickr-yearInput');
  const existingMonthSelect = currentMonthEl.querySelector('.dhds-flatpickr-monthSelect');

  if (!existingYearInput || !existingMonthSelect) {
    if (currentMonthEl.dataset) currentMonthEl.dataset.dhdsHeader = 'true';

    const yearInput = doc.createElement('input');
    yearInput.className = 'dhds-flatpickr-yearInput showSpinner';
    yearInput.setAttribute('type', 'number');
    yearInput.setAttribute('inputmode', 'numeric');
    yearInput.setAttribute('aria-label', 'Year');
    yearInput.setAttribute('step', '1');
    yearInput.setAttribute('min', '1000');
    yearInput.setAttribute('max', '9999');
    yearInput.value = yearText;
    yearInput.addEventListener('click', stopHeaderControlClickFromClosingCalendar);
    yearInput.addEventListener('mousedown', stopHeaderControlClickFromClosingCalendar);

    // Keep it visible even if global input styles are aggressive.
    setImportantStyle(yearInput, 'display', 'inline-block');
    setImportantStyle(yearInput, 'opacity', '1');
    setImportantStyle(yearInput, 'visibility', 'visible');
    setImportantStyle(yearInput, 'background-color', 'transparent');
    setImportantStyle(yearInput, 'border', '0');
    setImportantStyle(yearInput, 'box-shadow', 'none');
    setImportantStyle(yearInput, 'padding', '0');
    setImportantStyle(yearInput, 'margin', '0');
    setImportantStyle(yearInput, 'color', 'inherit');
    setImportantStyle(yearInput, '-webkit-text-fill-color', 'currentColor');

    const monthSelect = doc.createElement('select');
    monthSelect.className = 'dhds-flatpickr-monthSelect';
    monthSelect.setAttribute('aria-label', 'Month');
    monthSelect.value = monthIndexText;
    monthSelect.addEventListener('click', stopHeaderControlClickFromClosingCalendar);
    monthSelect.addEventListener('mousedown', stopHeaderControlClickFromClosingCalendar);

    setImportantStyle(monthSelect, 'display', 'inline-block');
    setImportantStyle(monthSelect, 'opacity', '1');
    setImportantStyle(monthSelect, 'visibility', 'visible');
    setImportantStyle(monthSelect, 'background-color', 'transparent');
    setImportantStyle(monthSelect, 'border', '0');
    setImportantStyle(monthSelect, 'box-shadow', 'none');
    setImportantStyle(monthSelect, 'margin', '0');
    setImportantStyle(monthSelect, 'color', 'inherit');

    for (let i = 0; i < 12; i += 1) {
      const option = doc.createElement('option');
      option.value = String(i);
      option.textContent = monthLonghand[i] || String(i + 1);
      monthSelect.appendChild(option);
    }

    monthSelect.value = monthIndexText;

    yearInput.addEventListener('change', (event) => {
      applyYearFromInput(instance, event && event.target);
    });

    // Spinner arrows tend to fire `input` rather than `change`.
    yearInput.addEventListener('input', (event) => {
      const target = event && event.target;
      if (!target) return;
      if (!isLikelyFullYear(target.value)) return;
      applyYearFromInput(instance, target);
    });

    yearInput.addEventListener('keydown', (event) => {
      if (!event || event.key !== 'Enter') return;
      applyYearFromInput(instance, event.target);
    });

    monthSelect.addEventListener('change', (event) => {
      const nextMonth = parseInt(event && event.target && event.target.value, 10);
      if (Number.isFinite(nextMonth)) safeChangeMonth(instance, nextMonth);
    });

    // Preserve Flatpickr's original header DOM nodes.
    // Flatpickr updates those nodes internally on redraw; removing them can break
    // in some contexts, so we only hide them via CSS and add our own controls.
    currentMonthEl.appendChild(yearInput);
    currentMonthEl.appendChild(monthSelect);
  }

  const yearInput = currentMonthEl.querySelector('.dhds-flatpickr-yearInput');
  const monthSelect = currentMonthEl.querySelector('.dhds-flatpickr-monthSelect');

  if (yearInput && yearInput.value !== yearText) yearInput.value = yearText;
  if (monthSelect && monthSelect.value !== monthIndexText) monthSelect.value = monthIndexText;
}
